/**
 * When we are allowed to interrupt a learner, and with what.
 *
 * `docs/02-user-flows.md` §8 specifies a whole re-engagement flow and publishes its
 * contract in-app: never more than two a day, never between 22:00 and 08:00 local,
 * every type individually togglable, and always a deep link to a specific action rather
 * than the home screen. None of it existed. Onboarding asked the learner what time to
 * remind them, stored the answer in `courses.reminder_local_time`, and nothing ever read
 * it — asking and then ignoring the answer, which is the pedagogical failure this
 * product was built to avoid repeating.
 *
 * The decision lives here rather than in the Worker because it is a rule about the
 * product, not about D1: it is testable as a table of cases, and the cases are the
 * promise. The *sending* is the Worker's.
 */

/**
 * Every kind of notification we will ever send, because every one must be togglable
 * individually — a list the learner can see is the only way that promise is checkable.
 */
export const NOTIFICATION_TYPES = [
  'streak_at_risk',
  'streak_broken',
  'reviews_due',
  'inactive_nudge',
  'content_drop',
  'trial_reminder',
] as const;

/**
 * The types that are a notice rather than a nudge.
 *
 * A trial reminder warns about money leaving someone's account on a date. It is not
 * competing for attention with the other five and must not lose a slot to them, so it
 * is exempt from the daily cap — but it still honours an explicit mute and quiet hours,
 * because a learner who muted billing notices has made a choice and 3am is nobody's
 * idea of a useful warning. Email carries the same message for anyone who muted it.
 *
 * `docs/research/12-competitor-recordings-2026-10-19.md` §3: subscription and refund
 * complaints are 17–23% of both competitors' negative reviews, and an unsent reminder
 * is how that starts.
 */
export const TRANSACTIONAL_TYPES: ReadonlySet<NotificationType> = new Set(['trial_reminder']);

/**
 * Where each transactional notification lands.
 *
 * Declared beside the type rather than at the one call site that sends it, because the
 * gate that checks every notification goes somewhere real walks the *policy* — and a
 * route that only exists inside a cron is a route that gate cannot see. §8's contract
 * is that a notification lands on the action it interrupted the learner about, so a
 * trial reminder lands where cancelling is one tap.
 */
export const TRANSACTIONAL_ROUTES: Readonly<Record<string, string>> = {
  trial_reminder: '/me/plans',
};
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** One notification we could send, fully formed. */
export interface NotificationCandidate {
  readonly type: NotificationType;
  /**
   * Where tapping it goes. Never `/`: a notification that drops the learner on the
   * home screen has made them find the thing it just interrupted them about.
   */
  readonly route: string;
  readonly title: string;
  readonly body: string;
  /**
   * Stable per (learner, type, local day), so an hourly job that runs twenty-four
   * times cannot send the same nudge twenty-four times.
   */
  readonly dedupeKey: string;
}

export interface NotificationWindow {
  /** The learner's own hour, 0–23. Quiet hours are theirs, not UTC's. */
  readonly localHour: number;
  /** How many we have already sent them today. */
  readonly sentToday: number;
  readonly mutedTypes: readonly NotificationType[];
}

/** The published contract, as numbers, so the tests and the UI can both read it. */
export const NOTIFICATION_LIMITS = {
  maxPerDay: 2,
  /** Inclusive. 22:00 is quiet. */
  quietFromHour: 22,
  /** Exclusive. 08:00 is not quiet. */
  quietUntilHour: 8,
} as const;

/** True when the hour falls inside the learner's published quiet window. */
export function isQuietHour(localHour: number): boolean {
  return (
    localHour >= NOTIFICATION_LIMITS.quietFromHour || localHour < NOTIFICATION_LIMITS.quietUntilHour
  );
}

/**
 * The subset of `candidates` we may actually send, in the order we would send them.
 *
 * Ordered by the candidate list itself rather than by a priority field: the caller
 * knows what matters most for this learner right now, and a second ranking here would
 * be a second place for that decision to live.
 */
export function allowedNotifications(
  candidates: readonly NotificationCandidate[],
  window: NotificationWindow,
): readonly NotificationCandidate[] {
  if (isQuietHour(window.localHour)) return [];

  const remaining = NOTIFICATION_LIMITS.maxPerDay - window.sentToday;
  if (remaining <= 0) return [];

  const muted = new Set(window.mutedTypes);
  const out: NotificationCandidate[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates) {
    if (out.length >= remaining) break;
    if (muted.has(candidate.type)) continue;
    // A guard, not a feature: the gate checks every type's route against the shared
    // route table, so a home-screen route here means someone broke the promise in code
    // rather than that we meant to allow it.
    if (candidate.route === '/' || candidate.route === '') continue;
    if (seen.has(candidate.dedupeKey)) continue;
    seen.add(candidate.dedupeKey);
    out.push(candidate);
  }

  return out;
}

/** What we know about a learner when deciding whether to interrupt them. */
export interface LearnerSignals {
  readonly localDate: string;
  /** Days in the current streak, 0 when there is none. */
  readonly streakDays: number;
  readonly goalMetToday: boolean;
  /** True when the streak ended and no freeze was left to save it. */
  readonly streakJustBroken: boolean;
  readonly dueReviews: number;
  readonly daysInactive: number;
  /** An unfinished lesson, which is a better nudge than a generic one. */
  readonly unfinished: { readonly title: string; readonly route: string } | null;
  readonly freshContent: { readonly count: number; readonly summary: string } | null;
}

/**
 * What is worth saying to this learner today, most important first.
 *
 * The copy is §8's, and its shape is the argument: a streak nudge names the streak and
 * the time it needs, a broken streak does not mourn it but points at what is still
 * there, and an inactivity nudge names the thing they left unfinished rather than
 * asking them to come back in general.
 */
export function notificationCandidates(signals: LearnerSignals): readonly NotificationCandidate[] {
  const out: NotificationCandidate[] = [];
  const key = (type: NotificationType) => `${type}:${signals.localDate}`;
  const minutes = Math.max(5, Math.round(signals.dueReviews / 5));

  if (signals.streakDays > 0 && !signals.goalMetToday) {
    out.push({
      type: 'streak_at_risk',
      route: '/review/session',
      title: `Your ${signals.streakDays}-day streak needs ${minutes} minutes`,
      body: 'Pick it up where you left off.',
      dedupeKey: key('streak_at_risk'),
    });
  }

  if (signals.streakJustBroken) {
    out.push({
      type: 'streak_broken',
      route: '/review/session',
      // Deliberately not an apology and not a guilt trip: the streak is gone and the
      // learning is not, and saying so is the difference between this and the
      // competitor's "you lost your streak!" screen.
      title: 'Streaks end. Learning doesn’t.',
      body:
        signals.dueReviews > 0
          ? `${signals.dueReviews} cards are waiting.`
          : 'Five minutes starts a new one.',
      dedupeKey: key('streak_broken'),
    });
  }

  if (signals.dueReviews > 0 && !signals.goalMetToday) {
    out.push({
      type: 'reviews_due',
      route: '/review/session',
      title: `${signals.dueReviews} cards are due`,
      body: `About ${minutes} minutes.`,
      dedupeKey: key('reviews_due'),
    });
  }

  if (signals.daysInactive >= 3 && signals.unfinished !== null) {
    out.push({
      type: 'inactive_nudge',
      route: signals.unfinished.route,
      // Names the specific unfinished thing. A generic "come back!" is the nag §8
      // explicitly refuses.
      title: `You left “${signals.unfinished.title}” part-finished`,
      body: 'It takes about three minutes to close.',
      dedupeKey: key('inactive_nudge'),
    });
  }

  if (signals.freshContent !== null && signals.freshContent.count > 0) {
    out.push({
      type: 'content_drop',
      route: '/learn/listen',
      title: 'New this week',
      body: signals.freshContent.summary,
      dedupeKey: key('content_drop'),
    });
  }

  return out;
}
